export const STATUS_META = {
  received: { label: "Received", tone: "neutral", pulse: true },
  parsing: { label: "Parsing", tone: "neutral", pulse: true },
  interpreting: { label: "AI reading", tone: "info", pulse: true },
  needs_review: { label: "Needs review", tone: "warning" },
  applying: { label: "Writing to Salesforce", tone: "info", pulse: true },
  applied: { label: "Applied", tone: "success" },
  rejected: { label: "Rejected", tone: "danger" },
  not_sales: { label: "Not a deal", tone: "slate" },
  failed: { label: "Failed", tone: "danger" },
};

export const INTENT_META = {
  new_opportunity: { label: "New opportunity", tone: "success" },
  update_opportunity: { label: "Update opportunity", tone: "info" },
  close_opportunity: { label: "Close opportunity", tone: "slate" },
  not_sales: { label: "Not sales", tone: "slate" },
  unclear: { label: "Unclear", tone: "warning" },
};
// intent pill text/tone, specialised for close-outs ("Close - Lost" / "Close - Won")
export function intentMeta(doc) {
  const ai = (doc && doc.ai) || {};
  const base = INTENT_META[ai.intent] || { label: ai.intent || "", tone: "neutral" };
  if (ai.intent !== "close_opportunity") return base;
  const outcome = ai.closeOut && ai.closeOut.outcome;
  if (outcome === "won") return { label: "Close - Won", tone: "success" };
  if (outcome === "lost") return { label: "Close - Lost", tone: "danger" };
  return { label: "Close opportunity", tone: "slate" };
}
export const MODE_META = {
  forward: { label: "Forwarded thread" },
  request: { label: "Direct request" },
};

export const FLAG_LABELS = {
  already_closed: "Opportunity already closed",
  no_opportunity_to_close: "No opportunity to close",
  amount_missing: "Amount missing for Closed Won",
  events_count_missing: "# of Curate events missing (required for Curate wins)",
  close_outcome_unclear: "Won or lost? unclear",
  contact_email_unknown: "Contact has no email",
  linkedin_unavailable: "LinkedIn profile could not be resolved",
  multiple_accounts: "Several matching accounts",
  no_external_party: "No prospect found",
  owner_not_found: "Owner not recognised",
  event_not_found: "Event not in Salesforce",
  stage_unclear: "Stage unclear",
  record_type_unclear: "Business line unclear (Sponsorship vs Curate)",
  possible_duplicate_opp: "Possible duplicate opportunity",
  conflicting_notes: "Notes conflict with thread",
  missing_company: "Company not identified",
};

export const ACTION_META = {
  create_account: { label: "Account", icon: "A" },
  create_contact: { label: "Contact", icon: "C" },
  create_opportunity: { label: "Opportunity", icon: "O" },
  update_opportunity: { label: "Opportunity", icon: "O" },
  close_opportunity: { label: "Opportunity", icon: "O" },
  create_contact_role: { label: "Contact role", icon: "R" },
  create_task: { label: "Task", icon: "T" },
};

// Business line (Opportunity record type) presentation
export const RECORD_TYPE_META = {
  sponsorship: { label: "Sponsorship", short: "Sponsorship", tone: "info", blurb: "Sponsoring / partnering on a Landmark-owned event" },
  curate: { label: "Curate", short: "Curate", tone: "success", blurb: "Landmark produces or recruits for the client's own event" },
  "client acquisition - bd": { label: "Client Acquisition - BD", short: "Client Acq.", tone: "slate", blurb: "Company wants to hire Landmark's BD / TAP service" },
};
export function recordTypeMeta(name) {
  const key = String(name || "").toLowerCase();
  return RECORD_TYPE_META[key] || { label: name || "", short: name || "", tone: "neutral", blurb: "" };
}
// "Curate2" -> "Curate 2"
export function curateLabel(cat) {
  return String(cat || "").replace(/^curate\s*(\d)/i, "Curate $1");
}
// picklists for a record type name (server provides meta.picklists.byRecordType), falling back to global lists
export function picklistsFor(meta, recordTypeName) {
  const pk = (meta && meta.picklists) || {};
  const by = pk.byRecordType || {};
  const key = Object.keys(by).find((k) => k.toLowerCase() === String(recordTypeName || "").toLowerCase());
  const rt = key ? by[key] : null;
  return {
    stages: (rt && rt.stages) || pk.stages || [],
    leadSources: (rt && rt.leadSources) || pk.leadSources || [],
    types: (rt && rt.types) || pk.types || [],
    curateCategories: (rt && rt.curateCategories && rt.curateCategories.length ? rt.curateCategories : pk.curateCategories) || ["Curate1", "Curate2"],
    plTypes: (rt && rt.plTypes && rt.plTypes.length ? rt.plTypes : pk.plTypes) || ["Curate Conferences", "Curate Dinners"],
  };
}
export function recordTypeNameForId(meta, recordTypeId) {
  const pk = (meta && meta.picklists) || {};
  const all = [...(pk.recordTypes || []), ...(pk.allRecordTypes || [])];
  const hit = all.find((r) => r.recordTypeId === recordTypeId);
  return hit ? hit.name : "";
}

export function isProcessing(status) {
  return ["received", "parsing", "interpreting", "applying"].includes(status);
}

export function relativeTime(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const diff = Date.now() - d.getTime();
  const s = Math.round(diff / 1000);
  if (s < 45) return "just now";
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.round(h / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export function formatDateTime(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export function initials(name, email) {
  const src = String(name || "").trim() || String(email || "").split("@")[0];
  const parts = src.replace(/[^a-zA-Z ]/g, " ").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function hueFromString(str) {
  let h = 0;
  for (const ch of String(str || "")) h = (h * 31 + ch.charCodeAt(0)) % 360;
  return h;
}

export function formatMs(ms) {
  if (!ms) return "";
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

export function formatAmount(n) {
  if (n === null || n === undefined || n === "") return "";
  const num = Number(n);
  if (!Number.isFinite(num)) return String(n);
  return num.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}
